import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import type { HandicapGuidanceItem } from './handicapGuidance.js';
import type { NavItem } from './types.js';
import { AppShellModel } from './shell.js';
import { useAppState } from './AppState.js';
import { useLeagueRole, useResolvedActiveSeason } from './hooks.js';
import { resolveSeasonChangePath } from './seasonNavigation.js';

const ADMIN_NAV_SECTIONS = [
  { id: 'league', label: 'League Settings' },
  { id: 'season', label: 'Season Settings' },
] as const;

function isAdminNavItemActive(item: NavItem, pathname: string): boolean {
  return pathname === item.path ||
    (item.id !== 'admin-home' && pathname.startsWith(`${item.path}/`));
}

export function StartupPanel({ title, message }: { title: string; message: string }) {
  return (
    <div className="panel">
      <h2>{title}</h2>
      <p className="muted">{message}</p>
    </div>
  );
}

export function ErrorPanel({ title, message }: { title: string; message: string }) {
  return (
    <section className="panel">
      <h3>{title}</h3>
      <div className="alert error">{message}</div>
    </section>
  );
}

export function LoadingPanel({ title = 'Loading', message = 'Fetching data.' }: { title?: string; message?: string }) {
  return <StartupPanel title={title} message={message} />;
}

export function GlobalBanner() {
  const { banner } = useAppState();
  if (!banner) {
    return null;
  }
  return (
    <div id="global-banner" className={`alert ${banner.type}`}>
      {banner.message}
    </div>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { session, player, leagues, activeLeagueId, setActiveLeague, signIn, signOut } = useAppState();
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useLeagueRole(activeLeagueId ?? '');
  const { seasonsQuery, selectedSeasonId, setSelectedSeasonId } = useShellSeasonState(activeLeagueId);

  const shellModel = new AppShellModel();
  shellModel.setSession(
    session
      ? {
          userId: session.userId,
          ...(player?.id ? { playerId: player.id } : {}),
          ...(player?.email ? { email: player.email } : {}),
        }
      : null,
  );
  shellModel.setMemberships(
    leagues.map((league) => ({
      leagueId: league.id,
      leagueName: league.name,
      role: league.id === activeLeagueId && role ? role : 'player',
    })),
  );
  shellModel.setActiveLeague(activeLeagueId);
  shellModel.setActiveSeason(selectedSeasonId);

  const nav = shellModel.getVisibleNav();
  const adminNav = shellModel.getVisibleAdminNav();
  const adminNavBySection = ADMIN_NAV_SECTIONS.map((section) => ({
    ...section,
    items: adminNav.filter((item) => item.section === section.id),
  })).filter((section) => section.items.length > 0);
  const userText = session ? (player?.name ?? session.name ?? session.email ?? 'Signed in') : '';
  const unlinked = Boolean(session && !player);

  return (
    <>
      <header className="app-header">
        <div className="header-row">
          <div className="brand">Elite League Manager</div>
          {leagues.length > 0 ? (
            <div className="header-selects">
              <select
                aria-label="Active league"
                id="league-switcher"
                value={activeLeagueId ?? ''}
                onChange={(event) => {
                  setActiveLeague(event.target.value);
                  navigate('/leagues');
                }}
              >
                {leagues.map((league) => (
                  <option key={league.id} value={league.id}>
                    {league.name}
                  </option>
                ))}
              </select>
              {activeLeagueId && seasonsQuery.data && seasonsQuery.data.length > 0 ? (
                <select
                  aria-label="Active season"
                  id="season-switcher"
                  value={selectedSeasonId ?? ''}
                  onChange={(event) => {
                    const nextSeasonId = event.target.value || null;
                    setSelectedSeasonId(activeLeagueId, nextSeasonId);
                    const nextPath = resolveSeasonChangePath(location.pathname, activeLeagueId, nextSeasonId);
                    if (nextPath !== location.pathname) {
                      navigate(nextPath, { replace: true });
                    }
                  }}
                >
                  {seasonsQuery.data.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          ) : null}
          <div className="header-user-actions">
            {userText ? (
              <span className="header-user" title={unlinked ? 'Player profile not linked' : undefined}>
                {userText}
                {unlinked ? ' · not linked' : ''}
              </span>
            ) : null}
            {session ? (
              <button
                className="secondary"
                type="button"
                onClick={async () => {
                  await signOut();
                  navigate('/');
                }}
              >
                Log out
              </button>
            ) : (
              <button type="button" onClick={() => void signIn()}>
                Log in
              </button>
            )}
          </div>
        </div>
        <nav className="nav-row" aria-label="League">
          {nav.map((item) => (
            <NavLink
              key={item.id}
              className={({ isActive }) =>
                `nav-link ${
                  isActive ||
                  (item.path.endsWith('/profile') &&
                    activeLeagueId &&
                    location.pathname.startsWith(`/leagues/${activeLeagueId}/players/`))
                    ? 'active'
                    : ''
                }`
              }
              to={item.path}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        {adminNavBySection.length > 0 ? (
          <nav className="nav-row admin-nav-row" aria-label="Admin">
            {adminNavBySection.map((section) => {
              const sectionIsActive = section.items.some((item) => isAdminNavItemActive(item, location.pathname));
              return (
                <details className="admin-nav-menu" key={section.id}>
                  <summary className={`nav-link admin-nav-link ${sectionIsActive ? 'active' : ''}`}>
                    {section.label}
                  </summary>
                  <div className="admin-nav-submenu">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.id}
                        className={({ isActive }) => `nav-link admin-nav-link ${isActive ? 'active' : ''}`}
                        end={item.id === 'admin-home'}
                        onClick={(event) => event.currentTarget.closest('details')?.removeAttribute('open')}
                        to={item.path}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                </details>
              );
            })}
          </nav>
        ) : null}
      </header>
      <GlobalBanner />
      <main className="grid app-main">
        {children}
      </main>
    </>
  );
}

function useShellSeasonState(activeLeagueId: string | null) {
  const { seasonsQuery, selectedSeasonId } = useResolvedActiveSeason(activeLeagueId ?? '');
  const app = useAppState();
  return {
    seasonsQuery,
    selectedSeasonId,
    setSelectedSeasonId: app.setSelectedSeasonId,
  };
}

export function PageIntro({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <section className="page-hero">
      <div className="page-hero-copy">
        <h2 className="page-hero-title">{title}</h2>
        {subtitle ? <p className="page-hero-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-hero-actions">{actions}</div> : null}
    </section>
  );
}
export function PanelHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="panel-header">
      <div>
        <h3>{title}</h3>
        {subtitle ? <p className="muted panel-header-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="panel-header-actions">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-state-title">{title}</div>
      {message ? <p className="muted">{message}</p> : null}
      {action ? <div className="inline-actions">{action}</div> : null}
    </div>
  );
}

export function HandicapGuidePanel({
  title = 'How Handicap Works',
  items,
}: {
  title?: string;
  items: HandicapGuidanceItem[];
}) {
  return (
    <section className="panel stack">
      <PanelHeader
        title={title}
      />
      <div className="definition-grid">
        {items.map((item) => (
          <article key={item.term} className="definition-card">
            <strong>{item.term}</strong>
            <p className="muted">{item.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function LoginPrompt({ message }: { message: string }) {
  const { signIn } = useAppState();
  return (
    <div className="login-wrap panel">
      <h1 className="login-title">Elite League Manager</h1>
      <p className="muted">Login is required to manage leagues, scoring, standings, and invites.</p>
      <div className="alert info">{message}</div>
      <div className="inline-actions" style={{ marginTop: '1rem' }}>
        <button type="button" onClick={() => void signIn()}>
          Log In
        </button>
      </div>
    </div>
  );
}

export function NotFoundPanel() {
  return (
    <>
      <PageIntro title="Not Found" />
      <section className="panel">
        <p className="muted">
          Use the navigation to continue or go back to the <Link to="/leagues">Dashboard</Link>.
        </p>
      </section>
    </>
  );
}
